import { useEffect, useState } from "react";
import { Download, X, Share, PlusSquare } from "lucide-react";
import { Button } from "../ui/button";
import { usePWAInstall, isIOSSafari, isStandalone } from "../../pwa";

const STORAGE_KEY = "mg_pwa_install_dismissed";
const DISMISS_DAYS = 7;

function wasDismissedRecently() {
  try {
    const ts = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 3600 * 1000;
  } catch { return false; }
}

export default function InstallBanner() {
  const { canInstall, install, installed } = usePWAInstall();
  const [dismissed, setDismissed] = useState(wasDismissedRecently);
  const [ios, setIos] = useState(false);
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    setIos(isIOSSafari());
    setStandalone(isStandalone());
  }, []);

  const showAndroid = canInstall && !dismissed && !installed && !standalone;
  const showIOS = ios && !dismissed && !standalone;

  if (!showAndroid && !showIOS) return null;

  const onDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300" /* z-stack-doc: L5 PWA install prompt */
      data-testid="pwa-install-banner"
    >
      <div className="relative rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-violet-50 shadow-xl p-4">
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-2 right-2 p-1 rounded-full hover:bg-slate-100 text-slate-400"
          aria-label="Cerrar"
          data-testid="pwa-install-dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shrink-0 shadow-sm">
            <Download className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-foreground leading-tight">
              Instala MenuGenius
            </h3>
            <p className="text-xs text-muted-foreground mt-1 leading-snug">
              {showAndroid
                ? "Acceso instantáneo desde tu pantalla de inicio. Sin barra de navegador, listo para fotos de facturas."
                : "Toca Compartir y elige \"Añadir a pantalla de inicio\" para tener MenuGenius como app."}
            </p>

            {showAndroid ? (
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  onClick={install}
                  className="h-8 gap-1.5"
                  data-testid="pwa-install-button"
                >
                  <Download className="h-3.5 w-3.5" />
                  Instalar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onDismiss}
                  className="h-8 text-muted-foreground"
                >
                  Luego
                </Button>
              </div>
            ) : (
              <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-card border border-border">
                  1. <Share className="h-3 w-3" /> Compartir
                </span>
                <span className="text-slate-400">→</span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-card border border-border">
                  2. <PlusSquare className="h-3 w-3" /> Añadir
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
